import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Space,
  Dropdown,
  App,
  Image,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  HomeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const PropertiesList = () => {
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    property_type_id: '',
    status: '',
    search: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { current, pageSize } = pagination;

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params: { [key: string]: any } = {
        page: current,
        limit: pageSize,
        ...filters,
      };

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await api.get('properties', { params });
      const { data, meta } = response.data;

      setProperties(data);
      setPagination(prev => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error: any) {
      console.error('Failed to fetch properties:', error);
      message.error('Gagal memuat data properti');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, filters, message]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm]);

  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const response = await api.get('property-types');
        setPropertyTypes(response.data.data);
      } catch (error: any) {
        console.error('Failed to fetch property types:', error);
      }
    };

    fetchPropertyTypes();
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setPagination(pagination);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDelete = async (id: any) => {
    try {
      await api.delete(`properties/${id}`);
      message.success('Properti berhasil dihapus');
      fetchProperties();
    } catch (error: any) {
      console.error('Failed to delete property:', error);
      message.error('Gagal menghapus properti');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      available: 'green',
      sold: 'red',
      reserved: 'orange',
    };
    return colors[status] || 'default';
  };

  const actionMenuItems = (record: any): any[] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'Detail',
      onClick: () => navigate(`/properties/${record.id}`),
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit',
      onClick: () => navigate(`/properties/${record.id}/edit`),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Hapus',
      danger: true,
      onClick: () => handleDelete(record.id),
    },
  ];

  const columns: any[] = [
    {
      title: 'Gambar',
      dataIndex: 'images',
      key: 'images',
      width: 100,
      render: (images: string) => {
        const imageList = images ? JSON.parse(images) : [];
        const firstImage = imageList[0];
        return firstImage ? (
          <Image
            width={80}
            height={60}
            src={firstImage}
            alt="Property"
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 60'%3E%3Crect width='80' height='60' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23666' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E"
          />
        ) : (
          <div style={{
            width: 80,
            height: 60,
            backgroundColor: '#f0f0f0',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <HomeOutlined style={{ fontSize: 24, color: '#ccc' }} />
          </div>
        );
      },
    },
    {
      title: 'Properti Info',
      key: 'property_info',
      width: 300,
      render: (_: any, record: any) => (
        <div>
          <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
            {record.title}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.address}, {record.city}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">{record.property_type?.name}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Property Area',
      dataIndex: ['property_area', 'Name'],
      key: 'property_area',
      width: 250,
      render: (text: string) => {
        if (!text) return '-';
        return (
          <Tag 
            color="geekblue" 
            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: 'Harga',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(price)}
        </Text>
      ),
      sorter: true,
    },
    {
      title: 'Spesifikasi',
      key: 'specification',
      render: (_: any, record: any) => (
        <div>
          <div>LT: {record.land_size}m² / LB: {record.building_size}m²</div>
          <div>{record.bedrooms} KT, {record.bathrooms} KM</div>
          {record.garage > 0 && <div>Garasi: {record.garage}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      filters: [
        { text: 'Available', value: 'available' },
        { text: 'Sold', value: 'sold' },
        { text: 'Reserved', value: 'reserved' },
      ],
    },
    {
      title: 'Created By',
      dataIndex: ['created_by', 'full_name'],
      key: 'created_by',
      render: (text: string) => text || '-',
    },
    {
      title: 'Tanggal Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
      sorter: true,
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="Detail">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/properties/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: actionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Manajemen Properti
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/properties/create')}
          >
            Tambah Properti
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Cari properti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Tipe Properti"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('property_type_id', value)}
              value={filters.property_type_id || undefined}
            >
              {propertyTypes.map((type: any) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('status', value)}
              value={filters.status || undefined}
            >
              <Option value="available">Available</Option>
              <Option value="sold">Sold</Option>
              <Option value="reserved">Reserved</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              onClick={() => {
                setFilters({
                  property_type_id: '',
                  status: '',
                  search: '',
                });
                setSearchTerm('');
              }}
            >
              Reset Filter
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={properties}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} properti`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>
    </div>
  );
};

export default PropertiesList;